import { useEffect, useMemo, useRef, useState } from "react";
import { AtSign, Check, Copy, Globe2, Image, Loader2, Plus, RefreshCw, ShieldCheck, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import i18n from "../i18n";

const copy = {
  fr: {
    logo: "Logo du Workspace",
    logoHint: "PNG, JPG ou WebP · 5 Mo maximum · optimisation automatique par SecurePass.",
    choose: "Choisir un logo",
    saveLogo: "Enregistrer le logo",
    removeLogo: "Supprimer le logo",
    preview: "Aperçu de la page de révélation",
    secureTitle: "Secret sécurisé",
    secureText: "Ce secret ne peut être révélé qu'une seule fois.",
    reveal: "Révéler le secret",
    powered: "Sécurisé par SecurePass",
    senderDomains: "Domaines d'envoi e-mail",
    senderHint: "Authentifiez votre domaine avec SPF/DKIM avant d'envoyer au nom de votre entreprise.",
    domainPlaceholder: "entreprise.com",
    addDomain: "Ajouter le domaine",
    noDomains: "Aucun domaine d'envoi configuré.",
    verified: "Vérifié",
    pending: "En attente DNS",
    failed: "À vérifier",
    checkDns: "Vérifier le DNS",
    delete: "Supprimer",
    dnsInstructions: "Enregistrements DNS à ajouter chez votre fournisseur DNS",
    copied: "Copié",
    senderAddresses: "Adresses expéditrices",
    localPart: "securepass",
    displayName: "Nom affiché",
    displayNamePlaceholder: "Mon entreprise",
    replyTo: "Reply-To (facultatif)",
    replyPlaceholder: "support@entreprise.com",
    addAddress: "Ajouter une adresse",
    defaultSender: "Par défaut",
    setDefault: "Définir par défaut",
    domainFirst: "Les adresses expéditrices seront disponibles une fois le domaine vérifié.",
    loading: "Chargement…",
  },
  en: {
    logo: "Workspace logo",
    logoHint: "PNG, JPG or WebP · 5 MB maximum · automatically optimized by SecurePass.",
    choose: "Choose a logo",
    saveLogo: "Save logo",
    removeLogo: "Remove logo",
    preview: "Reveal page preview",
    secureTitle: "Secure secret",
    secureText: "This secret can only be revealed once.",
    reveal: "Reveal secret",
    powered: "Secured by SecurePass",
    senderDomains: "Email sending domains",
    senderHint: "Authenticate your domain with SPF/DKIM before sending on behalf of your company.",
    domainPlaceholder: "company.com",
    addDomain: "Add domain",
    noDomains: "No sending domain configured.",
    verified: "Verified",
    pending: "Pending DNS",
    failed: "Needs attention",
    checkDns: "Check DNS",
    delete: "Delete",
    dnsInstructions: "DNS records to add at your DNS provider",
    copied: "Copied",
    senderAddresses: "Sender addresses",
    localPart: "securepass",
    displayName: "Display name",
    displayNamePlaceholder: "My company",
    replyTo: "Reply-To (optional)",
    replyPlaceholder: "support@company.com",
    addAddress: "Add address",
    defaultSender: "Default",
    setDefault: "Set as default",
    domainFirst: "Sender addresses become available once the domain is verified.",
    loading: "Loading…",
  },
};

function text() {
  return i18n.language?.toLowerCase().startsWith("en") ? copy.en : copy.fr;
}

const statusClass = (ok) => ok
  ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/30"
  : "bg-amber-500/15 text-amber-700 border-amber-500/30";

export const PersonalizationPanel = ({ workspaceId, role, entitlements = {} }) => {
  const t = text();
  const canManage = role === "owner" || role === "admin";
  const canBrand = !!entitlements.can_use_branding;
  const canSenderDomains = canBrand && (entitlements.max_sender_domains ?? entitlements.max_custom_domains ?? 0) !== 0;
  const maxSenderDomains = entitlements.max_sender_domains ?? entitlements.max_custom_domains ?? 0;

  const [branding, setBranding] = useState({ logo_url: "", logo_asset_id: null });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [domains, setDomains] = useState([]);
  const [domain, setDomain] = useState("");
  const [addingDomain, setAddingDomain] = useState(false);
  const [busyDomain, setBusyDomain] = useState(null);
  const [identityForms, setIdentityForms] = useState({});
  const fileRef = useRef(null);

  const load = async () => {
    if (!workspaceId) return;
    try {
      const [b, d] = await Promise.all([
        api.get("/account/branding"),
        canSenderDomains ? api.get(`/workspaces/${workspaceId}/sender-domains`) : Promise.resolve({ data: [] }),
      ]);
      setBranding(b.data.branding || { logo_url: "", logo_asset_id: null });
      setDomains(d.data || []);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [workspaceId, canSenderDomains]);

  useEffect(() => () => {
    if (logoPreview?.startsWith("blob:")) URL.revokeObjectURL(logoPreview);
  }, [logoPreview]);

  const shownLogo = logoPreview || branding.logo_url || "";
  const domainLimitReached = maxSenderDomains != null && domains.length >= maxSenderDomains;

  const pickLogo = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(i18n.language?.startsWith("en") ? "The file exceeds 5 MB." : "Le fichier dépasse 5 Mo.");
      return;
    }
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error(i18n.language?.startsWith("en") ? "Use PNG, JPG or WebP." : "Utilisez un fichier PNG, JPG ou WebP.");
      return;
    }
    if (logoPreview?.startsWith("blob:")) URL.revokeObjectURL(logoPreview);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", logoFile);
      const { data } = await api.post(`/workspaces/${workspaceId}/branding/logo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBranding(data.branding);
      setLogoFile(null);
      setLogoPreview("");
      toast.success(i18n.language?.startsWith("en") ? "Logo saved." : "Logo enregistré.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    try {
      const { data } = await api.delete(`/workspaces/${workspaceId}/branding/logo`);
      setBranding(data.branding);
      setLogoFile(null);
      setLogoPreview("");
      toast.success(i18n.language?.startsWith("en") ? "Logo removed." : "Logo supprimé.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const addDomain = async () => {
    if (!domain.trim() || domainLimitReached) return;
    setAddingDomain(true);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/sender-domains`, { domain: domain.trim() });
      setDomains((prev) => [data, ...prev]);
      setDomain("");
      toast.success(i18n.language?.startsWith("en") ? "Domain added. Configure its DNS records." : "Domaine ajouté. Configurez maintenant ses enregistrements DNS.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setAddingDomain(false);
    }
  };

  const verifyDomain = async (id) => {
    setBusyDomain(id);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/sender-domains/${id}/verify`);
      setDomains((prev) => prev.map((d) => d.id === id ? data : d));
      toast.success(data.verified
        ? (i18n.language?.startsWith("en") ? "Domain verified." : "Domaine vérifié.")
        : (i18n.language?.startsWith("en") ? "DNS is not fully propagated yet." : "Le DNS n'est pas encore totalement propagé."));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusyDomain(null);
    }
  };

  const deleteDomain = async (id) => {
    setBusyDomain(id);
    try {
      await api.delete(`/workspaces/${workspaceId}/sender-domains/${id}`);
      setDomains((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusyDomain(null);
    }
  };

  const copyValue = async (value) => {
    try {
      await navigator.clipboard.writeText(value || "");
      toast.success(t.copied);
    } catch (_) { /* noop */ }
  };

  const identityForm = (domainId) => identityForms[domainId] || { local_part: "securepass", display_name: "", reply_to: "" };
  const patchIdentityForm = (domainId, patch) => setIdentityForms((all) => ({
    ...all,
    [domainId]: { ...identityForm(domainId), ...patch },
  }));

  const addIdentity = async (d) => {
    const form = identityForm(d.id);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/sender-domains/${d.id}/identities`, form);
      setDomains((prev) => prev.map((x) => x.id === d.id ? { ...x, identities: [...(x.identities || []), data] } : x));
      patchIdentityForm(d.id, { local_part: "securepass", display_name: "", reply_to: "" });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const setDefaultIdentity = async (domainId, identityId) => {
    try {
      await api.post(`/workspaces/${workspaceId}/sender-identities/${identityId}/default`);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const deleteIdentity = async (domainId, identityId) => {
    try {
      await api.delete(`/workspaces/${workspaceId}/sender-identities/${identityId}`);
      setDomains((prev) => prev.map((x) => x.id === domainId
        ? { ...x, identities: (x.identities || []).filter((s) => s.id !== identityId) }
        : x));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!canBrand) {
    return <div className="glass rounded-2xl p-6 text-sm text-slate-500">{i18n.language?.startsWith("en") ? "Brand personalization is not included in this plan." : "La personnalisation de marque n'est pas incluse dans ce forfait."}</div>;
  }

  return (
    <div className="space-y-6" data-testid="personalization-panel">
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1">
          <Image className="w-5 h-5 text-cyan-600" />
          <h3 className="font-heading text-lg font-semibold">{t.logo}</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">{t.logoHint}</p>

        {canManage && (
          <div className="flex flex-wrap gap-2 mb-5">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={(e) => pickLogo(e.target.files?.[0])} />
            <Button variant="outline" className="border-slate-200" onClick={() => fileRef.current?.click()}>
              <Upload className="w-4 h-4 mr-2" /> {t.choose}
            </Button>
            {logoFile && (
              <Button onClick={uploadLogo} disabled={uploading} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950">
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}{t.saveLogo}
              </Button>
            )}
            {branding.logo_asset_id && (
              <Button variant="outline" onClick={removeLogo} className="border-red-200 text-red-600">
                <Trash2 className="w-4 h-4 mr-2" /> {t.removeLogo}
              </Button>
            )}
          </div>
        )}

        <Label className="text-slate-600 mb-2 block">{t.preview}</Label>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 sm:p-7">
          <div className="mx-auto max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
            {shownLogo ? (
              <img src={shownLogo} alt="Workspace" className="max-h-16 max-w-[220px] object-contain mx-auto mb-5" />
            ) : (
              <div className="flex items-center justify-center gap-2 mb-5 text-slate-700 font-semibold">
                <ShieldCheck className="w-6 h-6 text-emerald-600" /> SecurePass
              </div>
            )}
            <h4 className="font-heading font-bold text-lg mb-2">{t.secureTitle}</h4>
            <p className="text-sm text-slate-500 mb-5">{t.secureText}</p>
            <div className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-emerald-950">{t.reveal}</div>
            {shownLogo && <div className="text-[11px] text-slate-400 mt-5">🔒 {t.powered}</div>}
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1">
          <Globe2 className="w-5 h-5 text-violet-600" />
          <h3 className="font-heading text-lg font-semibold">{t.senderDomains}</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">{t.senderHint}</p>

        {!canSenderDomains ? (
          <p className="text-sm text-slate-500">{i18n.language?.startsWith("en") ? "Sending domains are not included in this plan." : "Les domaines d'envoi ne sont pas inclus dans ce forfait."}</p>
        ) : (
          <div className="space-y-4">
            {canManage && (
              <div className="flex gap-2">
                <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder={t.domainPlaceholder} className="bg-white border-slate-200" />
                <Button onClick={addDomain} disabled={addingDomain || domainLimitReached} className="bg-violet-500 hover:bg-violet-400 text-white shrink-0">
                  {addingDomain ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />{t.addDomain}</>}
                </Button>
              </div>
            )}
            {maxSenderDomains != null && <div className="text-xs text-slate-400">{domains.length} / {maxSenderDomains}</div>}
            {!domains.length && <p className="text-sm text-slate-500">{t.noDomains}</p>}

            {domains.map((d) => (
              <div key={d.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <Globe2 className="w-4 h-4 text-slate-400" />
                    <span className="font-medium truncate">{d.domain}</span>
                    <Badge variant="outline" className={statusClass(d.verified)}>{d.verified ? t.verified : t.pending}</Badge>
                  </div>
                  {canManage && <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={busyDomain === d.id} onClick={() => verifyDomain(d.id)} className="border-slate-200">
                      {busyDomain === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}{t.checkDns}
                    </Button>
                    <Button variant="ghost" size="sm" disabled={busyDomain === d.id} onClick={() => deleteDomain(d.id)} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>}
                </div>

                {!d.verified && (d.records || []).length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-slate-600 mb-2">{t.dnsInstructions}</div>
                    <div className="space-y-2">
                      {(d.records || []).map((r, idx) => (
                        <div key={`${r.name}-${idx}`} className="rounded-lg border border-slate-200 bg-white p-3 text-xs grid gap-1">
                          <div className="flex items-center gap-2"><Badge variant="outline">{r.type || r.record}</Badge><span className="font-medium">{r.name}</span></div>
                          <div className="flex items-start gap-2"><code className="break-all flex-1 text-slate-600">{r.value}</code><button onClick={() => copyValue(r.value)} className="text-slate-400 hover:text-slate-700"><Copy className="w-3.5 h-3.5" /></button></div>
                          {r.priority != null && <div className="text-slate-400">Priority: {r.priority}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {d.verified ? (
                  <div className="border-t border-slate-200 pt-4">
                    <div className="flex items-center gap-2 mb-3"><AtSign className="w-4 h-4 text-violet-600" /><span className="text-sm font-medium">{t.senderAddresses}</span></div>
                    <div className="space-y-2 mb-4">
                      {(d.identities || []).map((s) => (
                        <div key={s.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                          <div className="min-w-0"><div className="font-medium truncate">{s.display_name ? `${s.display_name} <${s.email}>` : s.email}</div>{s.reply_to && <div className="text-xs text-slate-400 truncate">Reply-To: {s.reply_to}</div>}</div>
                          <div className="flex items-center gap-1 shrink-0">{s.is_default ? <Badge className="bg-emerald-100 text-emerald-700">{t.defaultSender}</Badge> : canManage ? <Button size="sm" variant="ghost" onClick={() => setDefaultIdentity(d.id, s.id)}>{t.setDefault}</Button> : null}{canManage && <Button size="icon" variant="ghost" onClick={() => deleteIdentity(d.id, s.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>}</div>
                        </div>
                      ))}
                    </div>
                    {canManage && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="flex"><Input value={identityForm(d.id).local_part} onChange={(e) => patchIdentityForm(d.id, { local_part: e.target.value })} className="rounded-r-none bg-white" /><div className="rounded-r-md border border-l-0 border-slate-200 bg-slate-100 px-3 flex items-center text-xs text-slate-500">@{d.domain}</div></div>
                        <Input value={identityForm(d.id).display_name} onChange={(e) => patchIdentityForm(d.id, { display_name: e.target.value })} placeholder={t.displayNamePlaceholder} className="bg-white" />
                        <Input value={identityForm(d.id).reply_to} onChange={(e) => patchIdentityForm(d.id, { reply_to: e.target.value })} placeholder={t.replyPlaceholder} className="bg-white sm:col-span-2" />
                        <Button onClick={() => addIdentity(d)} className="sm:col-span-2 bg-violet-500 hover:bg-violet-400 text-white"><Plus className="w-4 h-4 mr-1" />{t.addAddress}</Button>
                      </div>
                    )}
                  </div>
                ) : <div className="text-xs text-slate-400">{t.domainFirst}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
